import React, { forwardRef, useId } from 'react';

import styled from '@mint-lib/styled';
import { CheckboxProps } from '@mui/material/Checkbox';
import { FormControlLabelProps } from '@mui/material/FormControlLabel';
import { SxProps } from '@mui/material/styles';

import Checkbox from '../Checkbox/Checkbox.jsx';
import FormControlLabel from '../FormControlLabel/FormControlLabel.jsx';
import Typography from '../Typography/Typography.jsx';

export interface CheckboxWithLabelProps {
  /**
   * If `true`, the checkbox is checked.
   * */
  checked?: boolean;
  /**
   * If `true`, the checkbox will be disabled.
   */
  disabled?: boolean;
  /**
   * The label text.
   */
  label?: string;
  /**
   * The value of the component.
   */
  value?: string;
  /**
   * The size of the component. small is equivalent to the dense checkbox styling.
   */
  size?: CheckboxProps['size'];
  /**
   * Change event.
   */
  onChange?: CheckboxProps['onChange'];
  /**
   * The position of the label.
   */
  labelPlacement?: 'end' | 'start' | 'top' | 'bottom';
  /**
   * The color of the component. It supports those theme colors that make sense for this component.
   */
  color?: CheckboxProps['color'];
  /**
   * If true, components appears indeterminate
   */
  indeterminate?: boolean;
  /**
   */
  name?: string;
  /**
   */
  formControlLabelProps?: Omit<
    FormControlLabelProps,
    'value' | 'control' | 'label'
  >;
  readOnly?: boolean;

  sx?: SxProps;
}

const CheckboxWithLabel = forwardRef(
  (
    props: CheckboxWithLabelProps,
    ref: React.ForwardedRef<HTMLButtonElement>,
  ) => {
    // Unique Id
    const id = useId();

    return (
      <FormControlLabel
        {...props.formControlLabelProps}
        htmlFor={id}
        ref={ref}
        control={
          <Checkbox
            id={id}
            inputProps={{ role: 'switch', 'aria-checked': props.checked }}
            size={props.size}
            color={props.color}
            checked={props.checked}
            onChange={props.onChange}
            indeterminate={props.indeterminate}
            name={props.name}
            disabled={props.disabled || props.readOnly}
            className={props.readOnly ? 'readonly-checkbox' : ''}
          />
        }
        label={
          <StyledTypography
            variant="bodyLong01"
            disabled={props.disabled || props.readOnly}
            readOnly={props.readOnly}
          >
            {props.label}
          </StyledTypography>
        }
        sx={{
          width: '100%',
          gap: '0.25rem',
          ...props.sx,
        }}
      />
    );
  },
);

const StyledTypography = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'readOnly' && prop !== 'disabled',
})<{
  disabled?: boolean;
  readOnly?: boolean;
}>(({ theme, disabled, readOnly }) => ({
  color: disabled
    ? theme.palette.text.disabled
    : readOnly
      ? theme.palette.text.secondary
      : theme.palette.text.primary,
}));

export default CheckboxWithLabel;
